import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Truck, Package, TrendingUp, CheckCircle, Star, 
  DollarSign, Users, ChevronDown, ArrowRight, Boxes,
  Globe, Shield, Clock, Cloud, Map, Activity,
  Building, Ship, Plane, Timer, Network, Play, Pause, Phone, Mail, MapPin, X, Code, Zap, Database, FileText, MessageSquare, AlertCircle, CheckCircle2,
  Terminal, 
  Server, 
  Send, 
  AlertTriangle,
  Smartphone,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import * as THREE from 'three';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { TextureLoader, ShaderMaterial, BackSide } from 'three';
import dayTextureUrl from '../assets/images/earth-blue-marble.jpg';
import nightTextureUrl from '../assets/images/BlackMarble_2016_01deg.jpg';
import { Html } from '@react-three/drei';
import { animated, useSpring } from '@react-spring/three';

import mobileOneUrl from '../assets/images/mobile-one.PNG';
import mobileTwoUrl from '../assets/images/mobile-two.PNG';
import collectionUrl from '../assets/images/collection.png';
import realtimeDashboardUrl from '../assets/images/realtime-dashboard.png';
import messagingPageUrl from '../assets/images/messaging-page.png';
import trackingPageUrl from '../assets/images/tracking-page.png';
import documentManagementUrl from '../assets/images/document-management.png';
import billingsOneUrl from '../assets/images/billings-one.png';
import billingsTwoUrl from '../assets/images/billings-two.png';
import billingsThreeUrl from '../assets/images/billings-three.png';
import billingsFourUrl from '../assets/images/billings-four.png';

// Add custom shader for atmosphere effect
const atmosphereVertexShader = `
varying vec3 vNormal;
void main() {
  vNormal = normalize(normalMatrix * normal);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const atmosphereFragmentShader = `
varying vec3 vNormal;
void main() {
  float intensity = pow(0.7 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
  gl_FragColor = vec4(0.3, 0.6, 1.0, 1.0) * intensity;
}
`;

// Enhanced Feature Card with more sophisticated interactions and visual effects
const FeatureCard = ({ icon: Icon, title, description }) => (
  <motion.div
    className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-indigo-500/50 transition-colors text-center flex flex-col items-center"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300 }}
  >
    <div className="bg-indigo-500/10 rounded-lg p-3 w-12 h-12 flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-indigo-400" />
    </div>
    <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
    <p className="text-gray-300">{description}</p>
  </motion.div>
);

// Enhanced CTA Button with animated gradient border
const CallToActionButton = ({ to, children, primary = true, onClick, secondary = false }) => (
  <motion.div
    className={`relative group w-full sm:w-auto ${
      primary ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white' : 'bg-transparent text-white border-2 border-white hover:bg-white/10'
    } ${secondary ? 'secondary' : ''}`}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-300" />
    <Link
      to={to}
      className={`relative flex items-center justify-center px-6 md:px-8 py-3 md:py-4 text-sm md:text-base font-medium rounded-lg transition duration-300 w-full sm:w-auto ${
        primary 
          ? 'bg-white text-indigo-600 hover:bg-gray-50' 
          : 'bg-transparent text-white border-2 border-white hover:bg-white/10'
      }`}
      onClick={onClick}
    >
      {children}
      <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform duration-300" />
    </Link>
  </motion.div>
);

// Add this before the GlobeObject component
const usePulseAnimation = () => {
  return useSpring({
    from: { opacity: 0.4 },
    to: { opacity: 1 },
    loop: { reverse: true },
    config: { duration: 1500 }
  });
};

// Add this helper function to calculate the pole direction
const calculatePoleDirection = (lat, lng) => {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  
  // Calculate the surface normal vector
  const nx = -Math.sin(phi) * Math.cos(theta);
  const ny = Math.cos(phi);
  const nz = Math.sin(phi) * Math.sin(theta);
  
  return new THREE.Vector3(nx, ny, nz);
};

// Updated Globe3D Component
const Globe3D = () => {
  return (
    <div className="relative w-full h-[500px] md:h-[800px] mx-auto touch-none">
      {/* Enhanced gradient overlays for seamless blend */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-transparent to-gray-900/90 z-10 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-transparent to-gray-900/90 z-10 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-gray-900/50 z-10 pointer-events-none" />
      
      <Canvas camera={{ position: [0, 0, 2.5], fov: 45 }}>
        <fog attach="fog" args={['#1e1b4b', 4, 7]} />
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        <GlobeObject />
        <OrbitControls 
          enableZoom={false}
          enablePan={false}
          autoRotate
          autoRotateSpeed={0.5}
          minPolarAngle={Math.PI / 3}
          maxPolarAngle={Math.PI / 1.5}
          enableDamping
          dampingFactor={0.05}
          domElement={null}
        />
      </Canvas>
    </div>
  );
};

// Updated GlobeObject component with integrated network visualization
const GlobeObject = () => {
  const globeRef = useRef();
  const pulseOpacity = usePulseAnimation();
  const [dayTexture, nightTexture] = useLoader(TextureLoader, [
    dayTextureUrl,
    nightTextureUrl
  ]);

  // Store hubs data in state to ensure proper re-rendering
  const [hubs, setHubs] = useState([]);

  // Initialize hubs with their positions and connections
  useEffect(() => {
    const logisticsHubs = [
      { id: 1, name: 'New York', lat: 40.7128, lng: -74.0060, type: 'major', volume: 95, height: 0.3, color: '#f472b6' },
      { id: 2, name: 'London', lat: 51.5074, lng: -0.1278, type: 'major', volume: 92, height: 0.28, color: '#818cf8' },
      { id: 3, name: 'Singapore', lat: 1.3521, lng: 103.8198, type: 'major', volume: 97, height: 0.32, color: '#60a5fa' },
      { id: 4, name: 'Dubai', lat: 25.2048, lng: 55.2708, type: 'major', volume: 88, height: 0.25, color: '#34d399' },
      { id: 5, name: 'Shanghai', lat: 31.2304, lng: 121.4737, type: 'major', volume: 94, height: 0.3, color: '#a855f7' },
      { id: 6, name: 'Rotterdam', lat: 51.9225, lng: 4.47917, type: 'regional', volume: 85, height: 0.2, color: '#ec4899' },
      { id: 7, name: 'Hong Kong', lat: 22.3193, lng: 114.1694, type: 'regional', volume: 89, height: 0.22, color: '#8b5cf6' },
      { id: 8, name: 'Los Angeles', lat: 34.0522, lng: -118.2437, type: 'major', volume: 91, height: 0.28, color: '#fbbf24' },
      { id: 9, name: 'São Paulo', lat: -23.5505, lng: -46.6333, type: 'major', volume: 87, height: 0.25, color: '#10b981' },
      { id: 10, name: 'Sydney', lat: -33.8688, lng: 151.2093, type: 'regional', volume: 86, height: 0.22, color: '#6366f1' }
    ];

    const hubsWithConnections = logisticsHubs.map(hub => ({
      ...hub,
      position: latLngToVector3(hub.lat, hub.lng, 1),
      connections: logisticsHubs
        .filter(target => target.id !== hub.id)
        .map(target => ({
          target: latLngToVector3(target.lat, target.lng, 1),
          curve: createConnectionCurve(
            hub.lat, hub.lng,
            target.lat, target.lng,
            hub.type === 'major' ? 0.4 : 0.3
          )
        }))
    }));

    setHubs(hubsWithConnections);
  }, []);

  // Create connection curve with dynamic height based on distance
  const createConnectionCurve = (lat1, lng1, lat2, lng2, maxHeight) => {
    const start = latLngToVector3(lat1, lng1, 1);
    const end = latLngToVector3(lat2, lng2, 1);
    const distance = start.distanceTo(end);
    const midHeight = 1 + (distance * maxHeight);
    
    const mid = new THREE.Vector3()
      .addVectors(start, end)
      .multiplyScalar(0.5)
      .normalize()
      .multiplyScalar(midHeight);

    return new THREE.CubicBezierCurve3(
      start,
      start.clone().lerp(mid, 0.33),
      end.clone().lerp(mid, 0.33),
      end
    );
  };

  // Create custom material for day/night blending
  const globeMaterial = useMemo(() => {
    return new ShaderMaterial({
      uniforms: {
        dayTexture: { value: dayTexture },
        nightTexture: { value: nightTexture },
        mixRatio: { value: 0 },
        sunDirection: { value: new THREE.Vector3(1, 0, 0) }
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vNormal;
        void main() {
          vUv = uv;
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D dayTexture;
        uniform sampler2D nightTexture;
        uniform float mixRatio;
        uniform vec3 sunDirection;
        varying vec2 vUv;
        varying vec3 vNormal;
        void main() {
          float dayNightMix = dot(vNormal, sunDirection) * 0.5 + 0.5;
          vec4 dayColor = texture2D(dayTexture, vUv);
          vec4 nightColor = texture2D(nightTexture, vUv);
          gl_FragColor = mix(nightColor, dayColor, dayNightMix);
        }
      `
    });
  }, [dayTexture, nightTexture]);

  // Animate sun position
  useFrame((state, delta) => {
    if (globeRef.current) {
      const time = state.clock.getElapsedTime();
      const sunDirection = new THREE.Vector3(
        Math.cos(time * 0.1),
        0,
        Math.sin(time * 0.1)
      );
      globeMaterial.uniforms.sunDirection.value = sunDirection;
      globeRef.current.rotation.y += delta * 0.1;
    }
  });

  return (
    <group ref={globeRef}>
      {/* Earth sphere with day/night cycle */}
      <mesh material={globeMaterial}>
        <sphereGeometry args={[1, 64, 64]} />
      </mesh>

      {/* Atmosphere effect */}
      <mesh scale={1.15}>
        <sphereGeometry args={[1, 64, 64]} />
        <shaderMaterial
          vertexShader={atmosphereVertexShader}
          fragmentShader={atmosphereFragmentShader}
          blending={THREE.AdditiveBlending}
          side={BackSide}
          transparent
        />
      </mesh>

      {/* Render hubs and connections */}
      {hubs.map((hub) => (
        <group key={`hub-${hub.id}`}>
          {/* Hub pole with corrected orientation */}
          <group position={hub.position}>
            <mesh
              position={hub.position.clone().multiplyScalar(0.01)} // Slight offset from surface
              quaternion={new THREE.Quaternion().setFromUnitVectors(
                new THREE.Vector3(0, 1, 0),
                calculatePoleDirection(hub.lat, hub.lng)
              )}
            >
              <cylinderGeometry args={[0.005, 0.005, hub.height, 8]} />
              <meshBasicMaterial color={hub.color} transparent opacity={0.8} />
            </mesh>
          </group>

          {/* Hub marker */}
          <animated.mesh position={hub.position.clone().multiplyScalar(1 + hub.height)}>
            <sphereGeometry args={[0.02, 16, 16]} />
            <meshBasicMaterial color={hub.color} transparent opacity={pulseOpacity.opacity} />
          </animated.mesh>

          {/* Hub label */}
          <Html position={hub.position.clone().multiplyScalar(1 + hub.height + 0.1)}>
            <div className="text-xs text-white bg-black/50 px-2 py-1 rounded-full whitespace-nowrap">
              {hub.name}
            </div>
          </Html>

          {/* Connections */}
          {hub.connections.map((connection, connIndex) => (
            <group key={`connection-${hub.id}-${connIndex}`}>
              <ConnectionCurve curve={connection.curve} color={hub.color} width={hub.type === 'major' ? 0.002 : 0.001} />
              <DataParticles
                curve={connection.curve}
                color={hub.color}
                speed={hub.type === 'major' ? 1 : 0.7}
                count={hub.type === 'major' ? 5 : 3}
              />
            </group>
          ))}
        </group>
      ))}
    </group>
  );
};

// New component for animated connection curves
const ConnectionCurve = ({ curve, color, width }) => {
  const points = useMemo(() => curve.getPoints(50), [curve]);
  
  return (
    <line>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={points.length}
          array={new Float32Array(points.flatMap(p => [p.x, p.y, p.z]))}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial color={color} transparent opacity={0.3} linewidth={width} />
    </line>
  );
};

// New component for animated data particles
const DataParticles = ({ curve, color, speed, count }) => {
  const particles = useRef([]);
  
  useEffect(() => {
    particles.current = Array.from({ length: count }, () => ({
      progress: Math.random(),
      speed: speed * (0.8 + Math.random() * 0.4)
    }));
  }, [count, speed]);

  useFrame((state, delta) => {
    particles.current.forEach(particle => {
      particle.progress += delta * particle.speed;
      if (particle.progress > 1) particle.progress = 0;
    });
  });

  return (
    <group>
      {particles.current.map((particle, i) => {
        const point = curve.getPoint(particle.progress % 1);
        return (
          <mesh key={i} position={point}>
            <sphereGeometry args={[0.004, 8, 8]} />
            <meshBasicMaterial color={color} transparent opacity={0.8} />
          </mesh>
        );
      })}
    </group>
  );
};

// Utility function to convert lat/lng to 3D coordinates
const latLngToVector3 = (lat, lng, radius) => {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  
  const x = -radius * Math.sin(phi) * Math.cos(theta);
  const y = radius * Math.cos(phi);
  const z = radius * Math.sin(phi) * Math.sin(theta);
  
  return new THREE.Vector3(x, y, z);
};

const TimelineItem = ({ icon: Icon, title, description, position, isLast }) => (
  <div className="relative flex items-start mb-12 last:mb-0">
    {/* Vertical line */}
    {!isLast && (
      <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gradient-to-b from-indigo-500/50 to-transparent" />
    )}
    
    {/* Icon */}
    <motion.div
      className="relative z-10 rounded-full p-4 bg-indigo-500/20 border border-indigo-500/50 hover:border-indigo-400 transition-colors"
      initial={{ scale: 0 }}
      whileInView={{ scale: 1 }}
      whileHover={{ scale: 1.1 }}
      transition={{ type: "spring", stiffness: 300 }}
      viewport={{ once: true }}
    >
      <Icon className="w-6 h-6 text-indigo-400" />
    </motion.div>
    
    {/* Content */}
    <motion.div 
      className="ml-8 bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 flex-1"
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      whileHover={{ borderColor: "rgba(99,102,241,0.5)" }}
      transition={{ duration: 0.2 }}
    >
      <h3 className="text-xl font-semibold text-white mb-3">{title}</h3>
      <p className="text-gray-300 leading-relaxed">{description}</p>
      
      {/* Status indicator */}
      <div className="mt-4 flex items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
        <span className="text-sm text-gray-400 font-medium">{position}</span>
      </div>
    </motion.div>
  </div>
);

const Timeline = () => (
  <div className="max-w-4xl mx-auto">
    <TimelineItem
      icon={FileText}
      title="Intelligent Load Management"
      description="AI-powered load matching and dynamic pricing optimization. Handles 100k+ concurrent auctions with sub-second response times."
      position="Real-time Processing"
    />
    
    <TimelineItem
      icon={Truck}
      title="Advanced Fleet Orchestration"
      description="Distributed tracking system processing 1M+ GPS updates per minute. Predictive ETA using machine learning models."
      position="Global Coverage"
    />
    
    <TimelineItem
      icon={MessageSquare}
      title="Enterprise Communication Hub"
      description="Fault-tolerant messaging infrastructure with guaranteed delivery. Supports 10M+ daily notifications across channels."
      position="High Availability"
    />
    
    <TimelineItem
      icon={AlertCircle}
      title="Automated Risk Management"
      description="ML-based anomaly detection and proactive issue resolution. Real-time fraud prevention and compliance monitoring."
      position="24/7 Monitoring"
    />
    
    <TimelineItem
      icon={DollarSign}
      title="Financial Operations Engine"
      description="Distributed payment processing handling $1B+ monthly volume. Real-time settlement with blockchain verification."
      position="Secure Processing"
    />
    
    <TimelineItem
      icon={CheckCircle2}
      title="Business Intelligence"
      description="Petabyte-scale data analytics with real-time dashboards. Custom reporting engine supporting 100k+ concurrent users."
      position="Enterprise Analytics"
      isLast
    />
  </div>
);

const TimelineSection = () => (
  <section className="py-16 md:py-32 bg-gradient-to-b from-indigo-900 to-purple-900 relative overflow-hidden">
    {/* Background particles */}
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]" />
    
    <div className="container mx-auto px-4 relative z-10">
      <motion.div
        className="text-center mb-20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
      >
        <h2 className="text-5xl font-bold text-white mb-6 tracking-tight">
          Enterprise Architecture
        </h2>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
          Scalable, resilient, and automated end-to-end logistics management
        </p>
      </motion.div>

      <Timeline />
    </div>
  </section>
);

// Add this component after LiveDataStream and before Home component
const TechnologyModal = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-start sm:items-center justify-center overflow-y-auto"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-900/90 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal Content - Wider padding and better spacing for mobile */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="relative w-full max-w-4xl bg-gradient-to-br from-gray-900 to-indigo-900/90 min-h-screen sm:min-h-0 sm:rounded-2xl shadow-2xl overflow-hidden my-0 sm:my-8"
          >
            {/* Simplified Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-6 bg-gradient-to-b from-gray-900 to-gray-900/80 backdrop-blur-sm border-b border-white/10">
              <h3 className="text-2xl sm:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
                API Documentation
              </h3>
              <button
                onClick={onClose}
                className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-200 active:scale-95"
                aria-label="Close modal"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <div className="p-6 sm:p-8 space-y-8">
              {/* Introduction - Simplified for mobile */}
              <div className="text-center space-y-4">
                <div className="w-24 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 mx-auto rounded-full" />
                <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto px-4">
                  Our comprehensive API documentation is currently under development.
                </p>
              </div>

              {/* Features - Larger cards with better spacing */}
              <div className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0">
                {[
                  {
                    title: "Real-time WebSocket API",
                    description: "Subscribe to live updates for shipments, bids, and market data.",
                    icon: Activity,
                    color: "from-indigo-500 to-purple-500"
                  },
                  {
                    title: "RESTful HTTP API",
                    description: "Comprehensive REST API for managing resources and data.",
                    icon: Cloud,
                    color: "from-blue-500 to-indigo-500"
                  },
                  {
                    title: "Token-based RBAC",
                    description: "Secure role-based access control with JWT authentication.",
                    icon: Shield,
                    color: "from-emerald-500 to-teal-500"
                  },
                  {
                    title: "Developer Tools",
                    description: "SDKs and interactive API playground for rapid development.",
                    icon: Code,
                    color: "from-purple-500 to-pink-500"
                  }
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/5 hover:bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 transition-all duration-300"
                  >
                    <div className="flex flex-col space-y-4">
                      <div className={`bg-gradient-to-br ${feature.color} rounded-xl p-3 w-12 h-12 flex items-center justify-center`}>
                        <feature.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold text-white mb-2">
                          {feature.title}
                        </h4>
                        <p className="text-base text-gray-400">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Newsletter - Simplified and more spacious */}
              <div className="bg-white/5 rounded-xl p-6 border border-white/10 space-y-6">
                <h4 className="text-xl font-semibold text-white text-center">
                  Get Notified When We Launch
                </h4>
                <div className="flex flex-col space-y-3">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                  />
                  <button className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 rounded-xl font-medium hover:opacity-90 transition-all duration-200 active:scale-95 text-base">
                    Notify Me
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Update the Home component to include modal state
const ProductPillarsSection = () => {
  const pillars = [
    {
      title: 'Real-time visibility',
      description: 'Low-latency updates for loads, bids, and messaging through persistent connections.',
      icon: Activity,
    },
    {
      title: 'Automation',
      description: 'Configurable flows for documents, notifications, and routine operations.',
      icon: Zap,
    },
    {
      title: 'Audit history',
      description: 'Track important changes across loads, billing, and documents with traceability.',
      icon: FileText,
    },
    {
      title: 'Access control',
      description: 'Role-oriented access patterns that keep the right actions with the right users.',
      icon: Shield,
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-gray-900 to-indigo-900 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center max-w-3xl mx-auto mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-3xl md:text-4xl font-bold text-white">What you get on day one</h3>
          <p className="text-gray-300 mt-3">Essential building blocks that teams use immediately</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 text-center"
            >
              <div className="w-12 h-12 rounded-lg bg-indigo-500/10 flex items-center justify-center mb-4 mx-auto">
                <p.icon className="w-6 h-6 text-indigo-400" />
              </div>
              <h4 className="text-white font-semibold mb-2">{p.title}</h4>
              <p className="text-gray-300 text-sm">{p.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowItWorksSection = () => {
  const steps = [
    {
      title: 'Create & Match',
      description: 'Post loads, auto-match carriers with dynamic pricing and constraints.',
      icon: FileText,
    },
    {
      title: 'Track & Orchestrate',
      description: 'Real-time GPS, geofencing, and exception handling across fleets.',
      icon: Truck,
    },
    {
      title: 'Bill & Settle',
      description: 'Automated documents, approvals, and payments with audit trails.',
      icon: DollarSign,
    },
  ];

  return (
    <section className="py-16 md:py-28 bg-gradient-to-b from-indigo-900 to-purple-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(167,139,250,0.12),transparent_70%)]" />
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          className="text-center max-w-3xl mx-auto mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">How it works</h3>
          <p className="text-gray-300">Launch fast, scale seamlessly, operate with confidence</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((step, idx) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="relative bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
            >
              <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white flex items-center justify-center font-semibold">
                {idx + 1}
              </div>
              <div className="mb-4 w-12 h-12 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                <step.icon className="w-6 h-6 text-indigo-400" />
              </div>
              <h4 className="text-white font-semibold mb-2">{step.title}</h4>
              <p className="text-gray-300 text-sm">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SecurityComplianceSection = () => {
  const features = [
    {
      title: 'Encryption & RBAC',
      description: 'Encryption in transit and at rest, with role-based access controls.',
      icon: Shield,
    },
    {
      title: 'Auditability',
      description: 'Structured activity records and change history across key workflows.',
      icon: Activity,
    },
    {
      title: 'Operational safeguards',
      description: 'Practical controls for data access, configuration, and recovery readiness.',
      icon: CheckCircle2,
    },
  ];

  return (
    <section className="py-16 md:py-28 bg-gradient-to-b from-gray-900 to-indigo-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(99,102,241,0.12),transparent_70%)]" />
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          className="text-center max-w-3xl mx-auto mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">Security fundamentals</h3>
          <p className="text-gray-300">Built with practical safeguards for logistics operations</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-2">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white/5 rounded-xl p-6 border border-white/10 text-center"
            >
              <div className="w-12 h-12 rounded-lg bg-indigo-500/10 flex items-center justify-center mb-4 mx-auto">
                <f.icon className="w-6 h-6 text-indigo-400" />
              </div>
              <h4 className="text-white font-semibold mb-2">{f.title}</h4>
              <p className="text-gray-300 text-sm">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Removed Integrations, Testimonials, and ROI sections to avoid unproven claims

const FAQSection = () => {
  const faqs = [
    {
      q: 'How long does implementation take?',
      a: 'Most teams launch their first region in under two weeks with our guided onboarding.',
    },
    {
      q: 'Do you support multi-tenant enterprise setups?',
      a: 'Yes. We offer tenant isolation, role hierarchies, and SSO integrations for enterprise orgs.',
    },
    {
      q: 'What is your uptime guarantee?',
      a: 'We operate with a 99.99% target availability and provide real-time status visibility.',
    },
    {
      q: 'Can we integrate with our existing TMS/ERP?',
      a: 'Absolutely. Use our APIs and webhooks, or work with our team for custom connectors.',
    },
  ];

  const [open, setOpen] = useState(null);

  return (
    <section className="py-16 md:py-28 bg-gray-900">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center max-w-3xl mx-auto mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">Frequently asked questions</h3>
          <p className="text-gray-300">Quick answers to common questions</p>
        </motion.div>

        <div className="max-w-3xl mx-auto divide-y divide-white/10 border border-white/10 rounded-xl bg-white/5 overflow-hidden">
          {faqs.map((item, i) => (
            <div key={i} className="p-5">
              <button
                className="flex w-full items-center justify-between text-left"
                onClick={() => setOpen(open === i ? null : i)}
              >
                <span className="text-white font-medium">{item.q}</span>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${open === i ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-3 text-gray-300 text-sm">{item.a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

function Home() {
  // Add this near the top of the Home component
  const [isTechModalOpen, setIsTechModalOpen] = useState(false);
  // Get user from Redux state
  const { user } = useSelector((state) => state.auth);

  // Animate on scroll
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  // Show loading spinner while JS/React is initializing
  if (typeof window === 'undefined') {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden bg-gray-50">
      {/* Hero Section */}
      <div className="relative min-h-screen">
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-30" />
        </div>

        {/* Floating elements */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              animate={{
                x: [0, Math.random() * 100 - 50],
                y: [0, Math.random() * 100 - 50],
                scale: [1, Math.random() + 0.5],
                opacity: [0.5, 0]
              }}
              transition={{
                duration: Math.random() * 2 + 2,
                repeat: Infinity,
                repeatType: "reverse"
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 container mx-auto px-4 h-screen flex flex-col justify-center">
          <motion.div 
            className="text-center max-w-5xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1 
              className="text-4xl md:text-7xl font-extrabold text-white mb-6 md:mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Welcome to{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 animate-gradient-x">
                LoadLink
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-2xl text-gray-300 mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Experience the future of logistics with AI-powered routing, real-time tracking, 
              and unparalleled efficiency.
            </motion.p>

            {/* Hero Section CTAs */}
            <motion.div 
              className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {user ? (
                <CallToActionButton to={`/${getUserRole(user.role)}/dashboard`} primary>
                  Go to Dashboard
                </CallToActionButton>
              ) : (
                <CallToActionButton to="/register" primary>
                  Start Shipping Today
                </CallToActionButton>
              )}
              {!user && (
                <CallToActionButton to="/login" primary={false}>
                  Login
                </CallToActionButton>
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Add DetailedFeatures section before EnhancedFeaturesSection */}
      {/* Product Pillars */}
      <ProductPillarsSection />

      {/* Detailed Features */}
      <DetailedFeatures />

      {/* Enhanced Features Section */}
      <EnhancedFeaturesSection />

      {/* Enhanced Statistics Section */}
      <EnhancedStatisticsSection />

      {/* Global Network Section with 3D Globe */}
      <section className="py-16 md:py-32 bg-gray-900">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl font-bold text-white mb-6">Global Network</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our intelligent network connects major logistics hubs worldwide, enabling seamless global trade
            </p>
          </motion.div>

          <Globe3D />
        </div>
      </section>

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Security Section */}
      <SecurityComplianceSection />

      {/* FAQ Section */}
      <FAQSection />

      {/* Real-Time Technology Section */}
      <section className="py-16 md:py-32 bg-gradient-to-b from-gray-900 to-indigo-900 relative overflow-hidden">
        {/* Enhanced background effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,_rgba(99,102,241,0.15),transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,_rgba(192,132,252,0.15),transparent_70%)]" />
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:radial-gradient(white,transparent_75%)]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Enhanced section header */}
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-block mb-4">
              <div className="flex items-center gap-2 text-sm text-indigo-400 bg-indigo-500/10 rounded-full px-4 py-1">
                <Server className="w-4 h-4" />
                Enterprise Infrastructure
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Real-Time Technology Platform
            </h2>
            <p className="text-xl text-gray-300">
              Experience our globally distributed, WebSocket-powered infrastructure delivering sub-millisecond updates
            </p>
          </motion.div>

          {/* Enhanced grid layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left column: Technical implementation */}
            <motion.div 
              className="lg:col-span-5 space-y-8"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {/* WebSocket Demo Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                    <Server className="w-5 h-5 text-indigo-400" />
                    WebSocket Implementation
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Real-time bidirectional communication with automatic reconnection
                  </p>
                </div>
                <div className="p-6 bg-gray-900/50">
                  <WebSocketDemo />
                </div>
                {/* Connection status indicator */}
                <div className="px-6 py-4 bg-gray-900/30 border-t border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-sm text-gray-400">Connected to production</span>
                  </div>
                  <span className="text-xs text-gray-500">Latency: 45ms</span>
                </div>
              </div>

              {/* System Metrics Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                    <Terminal className="w-5 h-5 text-indigo-400" />
                    System Metrics
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Real-time performance monitoring across global infrastructure
                  </p>
                </div>
                <div className="p-6">
                  <SystemMetrics />
                </div>
              </div>
            </motion.div>

            {/* Right column: Live demonstration */}
            <motion.div
              className="lg:col-span-7 space-y-8"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {/* Live Event Stream Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                    <Send className="w-5 h-5 text-indigo-400" />
                    Live Event Stream
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Real-time event processing across distributed systems
                  </p>
                </div>
                <div className="p-6 min-h-[300px]">
                  <LiveEventStream />
                </div>
                {/* Event statistics */}
                <div className="grid grid-cols-3 divide-x divide-white/10 border-t border-white/10">
                  {[
                    { label: 'Events/sec', value: '2.5k' },
                    { label: 'Avg. Latency', value: '45ms' },
                    { label: 'Success Rate', value: '99.99%' }
                  ].map((stat, i) => (
                    <div key={i} className="px-6 py-4 text-center">
                      <div className="text-sm text-gray-400">{stat.label}</div>
                      <div className="text-lg font-semibold text-white">{stat.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features Overview Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Enterprise Features
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Built for scale with enterprise-grade reliability
                  </p>
                </div>
                <div className="p-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    {[
                      {
                        icon: Zap,
                        title: "Real-Time Updates",
                        description: "Sub-millisecond data synchronization"
                      },
                      {
                        icon: Shield,
                        title: "Enterprise Security",
                        description: "End-to-end encryption & audit logs"
                      },
                      {
                        icon: Globe,
                        title: "Global Distribution",
                        description: "Multi-region active-active deployment"
                      },
                      {
                        icon: Database,
                        title: "Data Consistency",
                        description: "ACID compliance with zero data loss"
                      }
                    ].map((feature, i) => (
                      <div key={i} className="flex gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                            <feature.icon className="w-5 h-5 text-indigo-400" />
                          </div>
                        </div>
                        <div>
                          <h4 className="text-white font-medium mb-1">{feature.title}</h4>
                          <p className="text-gray-400 text-sm">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-purple-900 via-indigo-900 to-gray-900 relative overflow-hidden">
        {/* Enhanced background effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(167,139,250,0.15),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(99,102,241,0.15),transparent_60%)]" />
          <div className="absolute inset-0 bg-grid-white/5 [mask-image:radial-gradient(white,transparent_85%)]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Enterprise badge */}
            <div className="inline-block mb-6">
              <div className="flex items-center gap-2 text-sm text-purple-300 bg-purple-500/10 rounded-full px-4 py-1.5 border border-purple-500/20">
                <Truck className="w-4 h-4" />
                Enterprise Logistics Platform
              </div>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Ready to Transform Your <br className="hidden sm:block" />
              Global Supply Chain?
            </h2>
            
            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
              Join industry leaders revolutionizing their logistics operations with LoadLink's enterprise-grade platform.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <CallToActionButton 
                to="/register" 
                primary
                className="min-w-[200px] group"
              >
                <span>Get Started Now</span>
              </CallToActionButton>
              
              <CallToActionButton 
                to="/enterprise" 
                secondary
                className="min-w-[200px]"
              >
                Enterprise Solutions
              </CallToActionButton>
            </div>

            {/* Trust indicators */}
            <div className="pt-12 border-t border-white/10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-center">
                {[
                  { label: 'Enterprise Clients', value: '500+' },
                  { label: 'Countries Served', value: '50+' },
                  { label: 'Daily Shipments', value: '25K+' },
                  { label: 'Uptime SLA', value: '99.99%' }
                ].map((stat, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom decorative elements */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
      </section>

      {/* Footer Section */}
      <footer className="bg-gray-900 border-t border-white/10">
        <div className="container mx-auto px-4 py-12 md:py-20">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8 mb-12 md:mb-20">
            {/* Company Info */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
                LoadLink
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Enterprise-grade logistics platform with real-time operations, custom-built infrastructure, and intelligent automation.
              </p>
              <div className="flex space-x-4">
                {/* Social Media Links */}
                {[
                  { icon: 'M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z', name: 'Twitter' },
                  { icon: 'M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z', name: 'LinkedIn' },
                  { icon: 'M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z', name: 'GitHub' }
                ].map((social) => (
                  <a
                    key={social.name}
                    href="#"
                    className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors duration-300"
                    aria-label={social.name}
                  >
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d={social.icon} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            {/* Product Features */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Product Features</h4>
              <ul className="space-y-4">
                {[
                  'Real-Time Operations',
                  'Load Management',
                  'Billing & Disputes',
                  'Document Control',
                  'Mobile Applications',
                  'Telematics Integration'
                ].map((item) => (
                  <li key={item}>
                    <Link
                      to={`/features/${item.toLowerCase().replace(/\s+/g, '-')}`}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <ArrowRight className="w-4 h-4 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Enterprise */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Enterprise</h4>
              <ul className="space-y-4">
                {[
                  'Security & Compliance',
                  'API Documentation',
                  'User Management',
                  'Custom Integration',
                  'Admin Controls',
                  'Analytics Dashboard'
                ].map((item) => (
                  <li key={item}>
                    <Link
                      to={`/enterprise/${item.toLowerCase().replace(/\s+/g, '-')}`}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <ArrowRight className="w-4 h-4 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Contact Us</h4>
              <ul className="space-y-4">
                <li>
                  <a href="tel:+1234567890" className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    +1 (586) 770-7725
                  </a>
                </li>
                <li>
                  <a href="mailto:david@loadlinkco.com" className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    david@loadlinkco.com
                  </a>
                </li>
                <li className="flex items-start">
                  <MapPin className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-400">
                    LoadLink Company<br />
                    13950 Frazho Rd<br />
                    Warren, MI 48089
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/10">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-gray-400 text-sm">
                © {new Date().getFullYear()} LoadLink. Enterprise-grade logistics platform.
              </div>
              <div className="flex space-x-6">
                {[
                  'Terms of Service', 
                  'Privacy Policy', 
                  'Security', 
                  'API Status'
                ].map((item) => (
                  <Link
                    key={item}
                    to={`/${item.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-gray-400 hover:text-white text-sm transition-colors duration-300"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Technology Modal */}
      <TechnologyModal 
        isOpen={isTechModalOpen} 
        onClose={() => setIsTechModalOpen(false)} 
      />

    </div>
  );
}

const EnhancedFeaturesSection = () => (
  <section className="py-16 md:py-32 bg-gradient-to-b from-gray-900 to-indigo-900 relative overflow-hidden">
    {/* Enhanced background with better performance */}
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)] will-change-transform" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)] will-change-transform" />
    </div>

    <div className="container mx-auto px-4 relative z-10">
      <motion.div 
        className="text-center max-w-4xl mx-auto mb-20"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
      >
        <h2 className="text-5xl font-bold text-white mb-6 tracking-tight">
          Enterprise-Scale
        </h2>
        <p className="text-xl text-gray-300">
          Powering global supply chains with real-time visibility, AI-driven insights, and seamless automation
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
        <FeatureCard
          icon={Zap}
          title="Real-Time Operations"
          description="Live tracking and instant updates powered by distributed WebSocket architecture"
        />
        <FeatureCard
          icon={Shield}
          title="Enterprise Security"
          description="SOC2 certified platform with end-to-end encryption and advanced threat protection"
        />
        <FeatureCard
          icon={Database}
          title="Scalable Infrastructure"
          description="Cloud-native architecture handling millions of transactions across global regions"
        />
        <FeatureCard
          icon={Globe}
          title="Global Network"
          description="Distributed edge presence ensuring sub-second response times worldwide"
        />
        <FeatureCard
          icon={Code}
          title="API-First Platform"
          description="RESTful and GraphQL APIs with comprehensive SDK support across languages"
        />
        <FeatureCard
          icon={FileText}
          title="Smart Documents"
          description="AI-powered document processing with real-time validation and verification"
        />
        <FeatureCard
          icon={MessageSquare}
          title="Unified Communications"
          description="Multi-channel messaging system with guaranteed message delivery"
        />
        <FeatureCard
          icon={Smartphone}
          title="Mobile-Ready"
          description="Progressive Web App with offline capabilities and native app integration"
        />
      </div>
    </div>
  </section>
);

const StatCard = ({ icon: Icon, value, label, suffix = "", prefix = "" }) => (
  <motion.div
    className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-indigo-500/50 transition-colors text-center flex flex-col items-center"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300 }}
  >
    <div className="flex items-center justify-between w-full mb-4">
      <div className="bg-indigo-500/10 rounded-lg p-3 w-12 h-12 flex items-center justify-center">
        <Icon className="w-6 h-6 text-indigo-400" />
      </div>
      <motion.div
        whileHover={{ scale: 1.1 }}
        className="bg-green-500/10 rounded-lg p-3"
      >
        <ArrowUpRight className="w-5 h-5 text-green-400" />
      </motion.div>
    </div>
    <div className="flex items-baseline justify-center gap-1 mb-3">
      {prefix && <span className="text-2xl font-medium text-white">{prefix}</span>}
      <span className="text-4xl font-bold text-white tracking-tight">{value}</span>
      {suffix && <span className="text-2xl font-medium text-white">{suffix}</span>}
    </div>
    <p className="text-gray-300">{label}</p>
  </motion.div>
);

const ParticleBackground = () => (
  <div className="absolute inset-0">
    {[...Array(30)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-2 h-2 bg-white/10 rounded-full"
        animate={{
          x: [0, Math.random() * 200 - 100],
          y: [0, Math.random() * 200 - 100],
          scale: [1, Math.random() * 1.5 + 0.5],
          opacity: [0.3, 0]
        }}
        transition={{
          duration: Math.random() * 3 + 2,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut"
        }}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`
        }}
      />
    ))}
  </div>
);

const EnhancedStatisticsSection = () => (
  <section className="py-16 md:py-32 bg-gradient-to-b from-indigo-900 to-purple-900 relative overflow-hidden">
    <ParticleBackground />
    
    <div className="container mx-auto px-4 relative z-10">
      <motion.div 
        className="text-center max-w-4xl mx-auto mb-20"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
      >
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
          Global Scale Performance
        </h2>
        <p className="text-xl text-gray-300">
          Powering enterprise logistics with unmatched reliability and real-time capabilities
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
        <StatCard
          icon={Clock}
          value="99.9"
          suffix="%"
          label="Real-Time Data Accuracy"
        />
        <StatCard
          icon={Shield}
          value="99.99"
          suffix="%"
          label="Platform Availability"
        />
        <StatCard
          icon={Database}
          prefix=">"
          value="5"
          suffix="PB"
          label="Data Processed Monthly"
        />
        <StatCard
          icon={Globe}
          value="150"
          suffix="ms"
          label="Global Response Time"
        />
      </div>
    </div>
  </section>
);

// Simulated events for the demo
const eventTypes = [
  { type: 'LOAD_POSTED', message: 'New load posted', icon: FileText },
  { type: 'BID_RECEIVED', message: 'Bid received from carrier', icon: Send },
  { type: 'DOC_GENERATED', message: 'Rate confirmation generated', icon: FileText },
  { type: 'LOCATION_UPDATE', message: 'Driver location updated', icon: Truck },
  { type: 'BILLING_UPDATE', message: 'Invoice processed', icon: Terminal },
  { type: 'MESSAGE_RECEIVED', message: 'New message from carrier', icon: MessageSquare }
];

const LiveEventStream = () => {
  const [events, setEvents] = useState([]);
  
  useEffect(() => {
    const interval = setInterval(() => {
      const newEvent = eventTypes[Math.floor(Math.random() * eventTypes.length)];
      const timestamp = new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      setEvents(prev => [{
        id: Date.now(),
        ...newEvent,
        timestamp
      }, ...prev].slice(0, 5));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-2">
      {events.map((event) => {
        const Icon = event.icon;
        return (
          <motion.div
            key={event.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3 p-2 rounded bg-white/5"
          >
            <Icon className="w-4 h-4 text-indigo-400" />
            <span className="text-gray-300 flex-1">{event.message}</span>
            <span className="text-gray-500 text-sm">{event.timestamp}</span>
          </motion.div>
        );
      })}
    </div>
  );
};

const SystemMetrics = () => {
  const [metrics, setMetrics] = useState({
    activeConnections: 0,
    messagesProcessed: 0,
    documentsGenerated: 0
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(prev => ({
        activeConnections: Math.floor(Math.random() * 100 + 900),
        messagesProcessed: prev.messagesProcessed + Math.floor(Math.random() * 5),
        documentsGenerated: prev.documentsGenerated + Math.floor(Math.random() * 2)
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="grid grid-cols-3 gap-4">
      {Object.entries(metrics).map(([key, value]) => (
        <div key={key} className="bg-white/5 rounded p-3">
          <div className="text-gray-400 text-sm mb-1">
            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
          </div>
          <div className="text-white font-mono text-lg">
            {value.toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
};

const WebSocketDemo = () => (
  <div className="space-y-2 font-mono text-sm">
    <div className="text-indigo-400">{"// WebSocket connection handling"}</div>
    <div className="text-purple-400">{"socket.on('connect', () => {"}</div>
    <div className="pl-4 text-gray-300">{"console.log('Connected to real-time network')"}</div>
    <div className="text-purple-400">{"})"}</div>
    <div className="text-purple-400">{"socket.on('load.update', (data) => {"}</div>
    <div className="pl-4 text-gray-300">{"updateStore(data) // Redux update"}</div>
    <div className="pl-4 text-gray-300">{"notifyUser(data.notifications)"}</div>
    <div className="text-purple-400">{"})"}</div>
  </div>
);

// Update the FeatureShowcase component with adjusted proportions
const FeatureShowcase = ({ title, description, image, images, features, reversed = false }) => (
  <motion.div 
    className="flex flex-col lg:flex-row items-center gap-16 py-20" // Increased gap and padding
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
  >
    {/* Content - Made narrower */}
    <div className={`flex-1 lg:flex-[0.8] ${reversed ? 'lg:order-2' : ''}`}>
      <h3 className="text-2xl font-bold text-white mb-4">
        {title}
      </h3>
      <p className="text-lg text-gray-300 mb-8 leading-relaxed">
        {description}
      </p>
      
      <div className="space-y-3">
        {features.map((feature, index) => (
          <motion.div
            key={index}
            className="flex items-start gap-3 bg-white/5 rounded-lg p-3"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="bg-indigo-500/10 rounded-lg p-2">
              <feature.icon className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <h4 className="text-white font-medium mb-0.5 text-sm">
                {feature.title}
              </h4>
              <p className="text-gray-400 text-xs leading-relaxed">
                {feature.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>

    {/* Screenshot/Demo - Made larger */}
    <div className={`flex-1 lg:flex-[1.2] ${reversed ? 'lg:order-1' : ''}`}>
      {images ? (
        <ImageCarousel images={images} />
      ) : (
        <motion.div
          className="relative rounded-xl overflow-hidden shadow-2xl"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <img 
            src={image} 
            alt={title}
            className="w-full rounded-xl"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/20 via-transparent to-transparent" />
        </motion.div>
      )}
    </div>
  </motion.div>
);

// Update ImageCarousel for better proportions
const ImageCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const slideTimer = useRef(null);

  useEffect(() => {
    // Auto-advance slides
    slideTimer.current = setInterval(() => {
      handleNext();
    }, 5000);

    return () => clearInterval(slideTimer.current);
  }, [currentIndex]);

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  // Determine if current image is mobile screenshot
  const isMobileImage = images[currentIndex].url.includes('mobile-');

  return (
    <div className="relative group">
      {/* Main image container with conditional styling */}
      <div className="relative overflow-hidden rounded-xl shadow-2xl bg-gray-900">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className={`relative ${isMobileImage ? 'aspect-[9/16] max-w-[400px] mx-auto' : 'aspect-[16/9]'}`}
          >
            <img
              src={images[currentIndex].url}
              alt={images[currentIndex].title}
              className={`w-full h-full ${isMobileImage ? 'object-contain' : 'object-cover'}`}
            />
            {/* Caption overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent p-4">
              <h4 className="text-white text-sm font-medium mb-1">
                {images[currentIndex].title}
              </h4>
              <p className="text-gray-300 text-xs">
                {images[currentIndex].description}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation buttons */}
        <button
          onClick={handlePrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
        <button
          onClick={handleNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          aria-label="Next image"
        >
          <ChevronRight className="w-7 h-7" />
        </button>
      </div>

      {/* Thumbnail navigation */}
      <div className="flex justify-center mt-3 gap-1.5">
        {images.map((image, index) => (
          <button
            key={index}
            onClick={() => {
              if (isAnimating) return;
              setIsAnimating(true);
              setCurrentIndex(index);
              setTimeout(() => setIsAnimating(false), 500);
            }}
            className={`relative h-1 rounded-full transition-all duration-200 ${
              index === currentIndex ? "w-6 bg-indigo-500" : "w-3 bg-gray-600 hover:bg-gray-500"
            }`}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// Add new section for detailed features
const DetailedFeatures = () => (
  <section className="py-20 bg-gradient-to-b from-gray-900 to-indigo-900 relative overflow-hidden">
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]" />
    </div>

    <div className="container mx-auto px-4 relative z-10">
      <motion.div
        className="text-center max-w-4xl mx-auto mb-20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          Enterprise Features
        </h2>
        <p className="text-xl text-gray-300">
          Custom-built infrastructure powering global logistics operations
        </p>
      </motion.div>

      {/* Real-time Operations */}
      <FeatureShowcase
        title="Real-time Network Operations"
        description="Experience seamless real-time updates across your entire logistics network powered by WebSocket architecture and Redux state management."
        images={[
          {
            url: realtimeDashboardUrl,
            title: "Real-time Dashboard",
            description: "Live load updates, bids, and notifications without refresh"
          },
          {
            url: messagingPageUrl,
            title: "Real-time Notifications",
            description: "Live notifications and alerts for new shipments and updates"
          },
          {
            url: trackingPageUrl,
            title: "Real-time Tracking",
            description: "Live GPS tracking and vehicle telemetry data"
          }
        ]}
        features={[
          {
            icon: Zap,
            title: "Instant Updates",
            description: "Live load updates, bids, and notifications without refresh"
          },
          {
            icon: Network,
            title: "WebSocket Architecture",
            description: "Bi-directional communication for real-time data flow"
          },
          {
            icon: Activity,
            title: "Telematics Integration",
            description: "Live GPS tracking and vehicle telemetry data"
          }
        ]}
      />

      {/* Document Management */}
      <FeatureShowcase
        title="Intelligent Document Management"
        description="Automated document generation and processing with real-time updates and secure access from any device."
        image={documentManagementUrl}
        features={[
          {
            icon: FileText,
            title: "Smart Documents",
            description: "Automated rate confirmation generation and updates"
          },
          {
            icon: Shield,
            title: "Secure Access",
            description: "Role-based document access and version control"
          },
          {
            icon: MessageSquare,
            title: "Collaborative Review",
            description: "Real-time document collaboration and approvals"
          }
        ]}
        reversed
      />

      {/* Billing & Disputes */}
      <FeatureShowcase
        title="Advanced Billing System"
        description="Comprehensive billing management with integrated dispute resolution and real-time updates."
        images={[
          {
            url: billingsFourUrl,
            title: "Billing Dashboard",
            description: "Real-time overview of all financial transactions and pending approvals"
          },
          {
            url: billingsThreeUrl,
            title: "Dispute Resolution",
            description: "Streamlined workflow for managing and resolving billing disputes"
          },
          {
            url: billingsTwoUrl,
            title: "Payment Processing",
            description: "Automated payment tracking and reconciliation"
          },
          {
            url: billingsOneUrl,
            title: "Advanced Filters",
            description: "Advanced controls for organization administrators to manage billing operations"
          }
        ]}
        features={[
          {
            icon: DollarSign,
            title: "Smart Billing",
            description: "Automated invoice generation and payment processing"
          },
          {
            icon: AlertCircle,
            title: "Dispute Resolution",
            description: "Integrated workflow for handling billing disputes"
          },
          {
            icon: CheckCircle,
            title: "Real-time Updates",
            description: "Live payment status and transaction tracking"
          }
        ]}
      />

      {/* Mobile & API */}
      <FeatureShowcase
        title="Mobile & API Integration"
        description="Native mobile apps and comprehensive API access for seamless integration."
        images={[
          {
            url: mobileTwoUrl,
            title: "Mobile App",
            description: "iOS and Android apps for driver operations"
          },
          {
            url: collectionUrl,
            title: "API Access",
            description: "RESTful and WebSocket APIs with complete documentation"
          },
          {
            url: mobileOneUrl,
            title: "Mobile Dashboard",
            description: "User-friendly dashboard for real-time operations"
          }
        ]}
        features={[
          {
            icon: Smartphone,
            title: "Mobile Apps",
            description: "iOS and Android apps for driver operations"
          },
          {
            icon: Code,
            title: "API Access",
            description: "RESTful and WebSocket APIs with complete documentation"
          },
          {
            icon: Send,
            title: "Real-time Data",
            description: "Live data streaming for external integrations"
          }
        ]}
        reversed
      />
    </div>
  </section>
);

// Add the getUserRole function before the Home component or import it from elsewhere
const getUserRole = (role) => {
  if (role === 'system_admin') return 'admin';
  return (role === 'customer' || role === 'customer_admin') ? 'customer' : 'carrier';
};

export default Home;